import { ENTITLEMENTS, isGlobalRole } from '@auth';
import { FEATURE_FLAGS } from '@constants';
import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

import { useUserStore } from '~/store/user';
import { useAccountHierarchy } from './useAccountHierarchy';
import { useLaunchDarkly } from './useLaunchDarkly';

type AccountAdminAccess = {
  accountEntitlements?: readonly string[] | null;
  isParent: boolean;
};

type OpAccountAdminEligibility = {
  accountEntitlements?: readonly string[] | null;
  roleName?: string | null;
};

type OpAccountAdminAccess = OpAccountAdminEligibility & {
  targetAccountId?: string | null;
};

/**
 * Account Admin is account-scoped: a store-role entitlement must never grant access to it.
 * Keeping the decision pure makes the full role matrix testable without mounting the sidebar.
 */
export const hasAccountAdminAccess = ({ accountEntitlements, isParent }: AccountAdminAccess): boolean =>
  isParent && accountEntitlements?.includes(ENTITLEMENTS.account.update) === true;

/** OP staff qualify for the hand-off branch by global role + admin entitlement, never by parenthood. */
export const isOpAccountAdminEligible = ({ accountEntitlements, roleName }: OpAccountAdminEligibility): boolean =>
  isGlobalRole(roleName) && accountEntitlements?.includes(ENTITLEMENTS.adminStores.read) === true;

// OP staff have no tree of their own, so the hand-off branch also requires an explicit target account.
export const hasOpAccountAdminAccess = ({ targetAccountId, ...eligibility }: OpAccountAdminAccess): boolean =>
  isOpAccountAdminEligible(eligibility) && !!targetAccountId;

export const useAccountAdminAccess = (targetAccountId?: MaybeRefOrGetter<string | null | undefined>) => {
  const { user } = storeToRefs(useUserStore());
  const { isParent } = useAccountHierarchy();

  return computed(
    () =>
      hasAccountAdminAccess({
        accountEntitlements: user.value?.entitlements,
        isParent: isParent.value,
      }) ||
      hasOpAccountAdminAccess({
        accountEntitlements: user.value?.entitlements,
        roleName: user.value?.role?.name,
        targetAccountId: toValue(targetAccountId) ?? null,
      }),
  );
};

/** Normalizes a route's `accountId` query param: repeated or empty params read as no context. */
export const accountAdminTargetFromQuery = (query?: Record<string, unknown>): string | null => {
  const raw = query?.accountId;

  return typeof raw === 'string' && raw ? raw : null;
};

// The OP hand-off target tree, carried as `?accountId=`; parent users never carry one, so isOpContext stays false.
export const useAccountAdminContext = () => {
  const { user } = storeToRefs(useUserStore());
  const route = useRoute();

  const targetAccountId = computed(() => accountAdminTargetFromQuery(route.query));

  const isOpContext = computed(
    () =>
      !!targetAccountId.value &&
      isOpAccountAdminEligible({
        accountEntitlements: user.value?.entitlements,
        roleName: user.value?.role?.name,
      }),
  );

  return { isOpContext, targetAccountId };
};

/** Where "Manage in Account Admin" lands: the Brands tab, carrying the hand-off context. */
export const accountAdminHandoffRoute = (accountId: string) => ({
  path: '/account-admin/brands',
  query: { accountId },
});

/** Whether the current user should be offered the Admin → Stores hand-off links (ENG-5850 Track 2D). */
export const useAccountAdminHandoff = () => {
  const { user } = storeToRefs(useUserStore());
  const { getFlag } = useLaunchDarkly();

  const enabled = computed(
    () =>
      getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) === true &&
      isOpAccountAdminEligible({
        accountEntitlements: user.value?.entitlements,
        roleName: user.value?.role?.name,
      }),
  );

  return { enabled, routeFor: accountAdminHandoffRoute };
};
